import { ImagePicker } from 'components/reusable/ImagePicker';
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable, TextInput, StyleSheet } from 'react-native';
import { CheckBox } from 'react-native-web';

import { AreaType } from 'types/area';
import { Coordinate } from 'types/map';
import { EditCoordinate } from './EditCoordinate';
import { dataIsValid } from 'utils/helpers';
import { useAppDispatch, useAppSelector } from 'hooks/hooks';
import { change } from 'hooks/areasSlice';
import { initEdit } from 'hooks/editSlice';

function AreaRow({
  area,
  styles,
  deleteArea,
  save,
  cancelChanges,
  hasChanged,
  displayError,
}: {
  area: AreaType;
  styles: StyleSheet.NamedStyles<any>;
  deleteArea: (area: AreaType) => void;
  save: (area: AreaType) => Promise<void>;
  cancelChanges: (area: AreaType) => void;
  hasChanged: (area: AreaType) => boolean;
  displayError: (msg: string) => void;
}) {
  const dispatch = useAppDispatch();
  const edit = useAppSelector((state) => state.edit);
  const [isChangingCoords, setIsChangingCoords] = useState(false);
  const form = {
    icon: {
      required: true,
      errorMessage: 'Icon is required.',
      error: null,
    },
    coordinates: {
      required: true,
      errorMessage: 'Location is required.',
      error: null,
    },
  };
  const [formValidation, setFormValidation] = useState({ ...form });
  const onDataChange = useCallback(
    (key: string, value: string | Coordinate[] | boolean) => {
      if (value !== area[key]) {
        const newValue = {
          ...area,
          [key]: value,
        };
        dispatch(change({ area: newValue }));
      }
    },
    [dispatch, area],
  );

  async function getImage(
    key: string,
    maxSize: number,
    image: string,
    size: number,
  ) {
    if (image) {
      if (maxSize && size && size > maxSize) {
        const kb = Math.round((Math.ceil(maxSize / 4) * 3) / 1000);
        displayError(`Image is too large, please select one less than ${kb}kb`);
      } else {
        onDataChange(key, image);
      }
    }
  }

  useEffect(() => {
    if (!area.coordinates && !isChangingCoords) {
      setIsChangingCoords(true);
      dispatch(initEdit());
    }

    if (isChangingCoords && edit.value.coords?.length) {
      onDataChange('coordinates', edit.value.coords);
    }
  }, [edit, area, onDataChange, isChangingCoords, dispatch]);

  return (
    <View style={styles.row}>
      <Text style={styles.label}>Name:</Text>
      <TextInput
        style={[styles.input]}
        value={area.name}
        onChangeText={(text) => onDataChange('name', text)}
      />

      <Text style={styles.label}>Description:</Text>
      <TextInput
        style={[styles.input]}
        value={area.description || ''}
        onChangeText={(text) => onDataChange('description', text)}
        numberOfLines={3}
        multiline={true}
      />

      <View
        style={{ flexDirection: 'row', marginBottom: 5, alignItems: 'center' }}
      >
        <Text style={styles.label}>Block trail:</Text>
        <CheckBox
          value={area.block || false}
          onValueChange={(value) => onDataChange('block', value)}
          style={{ marginLeft: 5, marginBottom: 3 }}
        />
      </View>

      <View>
        <EditCoordinate
          coord={area.coordinates}
          onClear={() => {
            onDataChange('coordinates', null);
            setIsChangingCoords(true);
          }}
        />
        {formValidation.coordinates.error && (
          <Text style={styles.error}>
            {formValidation.coordinates.errorMessage}
          </Text>
        )}
        {!area.coordinates && (
          <Text style={styles.action}>Select area on map</Text>
        )}
      </View>

      <Text style={styles.label}>Icon:</Text>
      <ImagePicker
        onImageChange={(image, size) => getImage('icon', 5400, image, size)}
        image={area.icon}
        type="icon"
      />
      {formValidation.icon.error && (
        <Text style={styles.error}>{formValidation.icon.errorMessage}</Text>
      )}

      <View style={styles.inlineButtonContainer}>
        <Pressable onPress={() => deleteArea(area)}>
          <Text style={[styles.inlineButton, styles.error]}>Delete</Text>
        </Pressable>
        {hasChanged(area) && (
          <Pressable
            onPress={() => {
              setIsChangingCoords(false);
              cancelChanges(area);
            }}
          >
            <Text style={styles.inlineButton}>Cancel</Text>
          </Pressable>
        )}
        {hasChanged(area) && (
          <Pressable
            onPress={() => {
              if (dataIsValid(formValidation, setFormValidation, area)) {
                save(area).then(() => setIsChangingCoords(false));
              }
            }}
          >
            <Text style={[styles.inlineButton, styles.success]}>Save</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

export function EditAreas({
  styles,
  addArea,
  deleteArea,
  saveArea,
  displayError,
  cancelChanges,
  hasChanged,
}: {
  styles: StyleSheet.NamedStyles<any>;
  addArea: () => void;
  deleteArea: (area: AreaType) => void;
  saveArea: (area: AreaType) => Promise<void>;
  displayError: (msg: string) => void;
  cancelChanges: (area: AreaType) => void;
  hasChanged: (area: AreaType) => boolean;
}) {
  const dispatch = useAppDispatch();
  const areas = useAppSelector((state) => state.areas);
  function save(area: AreaType) {
    return saveArea(area)
      .then(() => {
        dispatch(change({ area }));
      })
      .catch(() => displayError('Area not saved, please try again'));
  }

  return (
    <View>
      {areas && (
        <View>
          {areas.value.map((area) => (
            <AreaRow
              key={area.id}
              area={area}
              styles={styles}
              deleteArea={deleteArea}
              save={save}
              cancelChanges={cancelChanges}
              hasChanged={hasChanged}
              displayError={displayError}
            />
          ))}
        </View>
      )}

      <View style={styles.buttonContainer}>
        <Pressable onPress={addArea} style={styles.button}>
          <Text style={styles.buttonText}>Add Area</Text>
        </Pressable>
      </View>
    </View>
  );
}
